import React, { useState } from 'react';
import { Modal, Typography, Button, Row, Col, Space } from 'antd';
import { MinusCircleOutlined, StarOutlined } from '@ant-design/icons';
import V from 'voca';

import { useRecoilState, useRecoilValue } from 'recoil';
import services from '../../services';
import states from '../../states';
import { parseFavorites } from '../../utils/exercises.utils';
import { getEmbeddedUrl } from '../../utils/videoUrl.utils';
import setNotification from '../../utils/setNotification.utils';

import NoResults from '../../components/NoResults';

const ExerciseDetailsModal = ({ handleExerciseDetailsModal }) => {
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const user = useRecoilValue(states.user);
  const exercises = useRecoilValue(states.exercises);
  const modals = useRecoilValue(states.modal);
  const [favorites, setFavorites] = useRecoilState(states.favorites);

  if (!exercises.details || !modals.exerciseDetails) {
    return null;
  }

  const { video, exerciseName, sets, reps, hold, instructions, id, groupId } =
    exercises.details;

  const item = favorites.list.find(fav => fav.id === id);
  const star = item ? true : !!exercises.details?.star;
  const embeddedUrl = exercises.details ? getEmbeddedUrl(video) : undefined;

  const handleAddFavorite = async () => {
    try {
      setFavoriteLoading(true);

      let response = await services.exercises.addFavorite(
        user.details.favoriteId,
        [{ Id: id, GroupId: groupId }]
      );

      if (response.status === 200) {
        setFavorites({
          ...favorites,
          list: parseFavorites(response.data.exercises)
        });
        handleExerciseDetailsModal(null);
        setNotification(
          'success',
          'Success!',
          'Exercise has been added to favorites.'
        );
      }
    } catch (err) {
      setNotification(
        'error',
        'Error!',
        'An error occurred while adding exercise to favorites.'
      );
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleRemoveFavorite = async () => {
    try {
      setFavoriteLoading(true);

      let response = await services.exercises.deleteFavorite(
        user.details.favoriteId,
        id
      );

      if (response.status === 200) {
        setFavorites({
          ...favorites,
          list: parseFavorites(response.data.exercises)
        });
        handleExerciseDetailsModal(null);
        setNotification(
          'success',
          'Success!',
          'Exercise has been removed from favorites.'
        );
      }
    } catch (err) {
      setNotification(
        'error',
        'Error!',
        'An error occurred while removing exercise to favorites.'
      );
    } finally {
      setFavoriteLoading(false);
    }
  };

  const exceedsFavoriteLimit = () => {
    return favorites.list && favorites.list.length >= 98;
  };

  return (
    <Modal
      visible
      onCancel={() => handleExerciseDetailsModal(null)}
      footer={null}
      className="preview-exercise-modal"
      width={700}
    >
      {embeddedUrl ? (
        <div className="preview-iframe-wrapper">
          <iframe
            title="Exercise Video"
            src={embeddedUrl}
            width="100%"
            frameBorder="0"
            allowFullScreen
          />
        </div>
      ) : (
        <NoResults content="No video embedded on this exercise." />
      )}
      <div className="preview-modal-body">
        <div className="tab-header">
          <Typography.Title level={4}>
            {exerciseName && V.titleCase(exerciseName)}{' '}
          </Typography.Title>
          {Array.isArray(favorites.list) && (
            <div style={{ width: 220 }}>
              <div>
                {star ? (
                  <Button
                    loading={favoriteLoading}
                    type="default"
                    className="ptw-btn pull-right"
                    size="large"
                    onClick={handleRemoveFavorite}
                    icon={<MinusCircleOutlined />}
                  >
                    Remove to Favorites
                  </Button>
                ) : (
                  <Button
                    loading={favoriteLoading}
                    type="primary"
                    className="ptw-btn btn-primary pull-right"
                    size="large"
                    onClick={() => {
                      if (exceedsFavoriteLimit()) {
                        setNotification(
                          'info',
                          'Notice!',
                          'You have reached your 100 favorite exercises limit.'
                        );
                      } else {
                        handleAddFavorite();
                      }
                    }}
                    icon={<StarOutlined />}
                  >
                    Add to Favorites
                  </Button>
                )}
              </div>
              <Typography.Paragraph
                className="mt-1"
                italic
                type="secondary"
                style={{
                  float: 'right',
                  textAlign: 'left',
                  fontSize: 11,
                  display: 'block'
                }}
              >
                Note: The adding of exercise to favorites is limited to 100.
              </Typography.Paragraph>
            </div>
          )}
        </div>
        <Space size={24} direction="vertical" className="preview-modal-space">
          <Row gutter={[8, 8]}>
            {sets && (
              <Col lg={8} md={8} sm={24} xs={24}>
                <Typography.Title level={5} className="text-center">
                  {sets} Sets
                </Typography.Title>
              </Col>
            )}
            {reps && (
              <Col lg={8} md={8} sm={24} xs={24}>
                <Typography.Title level={5} className="text-center">
                  {reps} Reps
                </Typography.Title>
              </Col>
            )}
            {hold && (
              <Col lg={8} md={8} sm={24} xs={24}>
                <Typography.Title level={5} className="text-center">
                  {hold} Hold
                </Typography.Title>
              </Col>
            )}
          </Row>
          <Row>
            <Col lg={24} md={24} sm={24} xs={24}>
              <Typography.Text>{instructions}</Typography.Text>
            </Col>
          </Row>
        </Space>
      </div>
    </Modal>
  );
};

export default ExerciseDetailsModal;
